import {noop} from '@benchling/js-utils';
import React, {Dispatch} from 'react';

import {
  AlignmentInput,
  AlignmentSetup,
  CreateSequenceAlignmentModalAction,
  CreateSequenceAlignmentModalActionType,
} from 'analyses/alignment/modals/createSequenceAlignmentsModalReducer';
import {
  getInputChip,
  getInputItemKey,
  isBioentityInput,
  isTemplateSetup,
} from 'analyses/alignment/modals/SharedUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {InputChangeEvent} from 'util/Types';

/**
 * Renders a cell with a chip for the given input.
 *
 * @param input Input to render a chip for.
 * @param isTemplate Whether this input is a template. Removal is disabled for templates.
 * @param dispatch Dispatch for the CreateSequenceAlignmentsModal state.
 * @constructor
 */
function UseCurrentVersionTableSequenceCell({
  input,
  isTemplate,
  dispatch,
}: {
  input: AlignmentInput;
  isTemplate: boolean;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  const onRemove = isTemplate
    ? undefined
    : () => {
        dispatch({type: CreateSequenceAlignmentModalActionType.REMOVE_INPUT, input});
      };
  return (
    <Cell>
      {getInputChip(input, {
        size: ChipSize.MINI,
        onRemove,
      })}
    </Cell>
  );
}

/**
 * Renders a checkbox cell for toggling the use current version state of an alignment input.
 *
 * @param input Input to display the checkbox for.
 * @param dispatch Dispatch for the CreateSequenceAlignmentsModal state.
 * @constructor
 */
function UseCurrentVersionTableCheckboxCell({
  input,
  dispatch,
}: {
  input: AlignmentInput;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  // Use current version flag is only applicable to sequence entities.
  let checked = false;
  let disabled = true;
  let onChange: (e: InputChangeEvent) => void = noop;
  if (isBioentityInput(input)) {
    checked = input.useCurrentVersion;
    disabled = false;
    onChange = (e: InputChangeEvent) => {
      dispatch({
        type: CreateSequenceAlignmentModalActionType.SET_USE_CURRENT_VERSION,
        input,
        value: e.target.checked,
      });
    };
  }

  return (
    <Cell>
      <CheckboxInput
        id={getInputItemKey(input)}
        checked={checked}
        disabled={disabled}
        label=""
        onChange={onChange}
      />
    </Cell>
  );
}

/**
 * Renders a table of input from the alignment setup with two columns.
 *
 * Columns
 *  1. Sequence - Displays the input item as a chip
 *  2. Use Latest Version - Displays a checkbox for toggling the useCurrentVersion state of bioentity inputs.
 *    This is disabled for non-bioentity inputs.
 * @param alignmentSetup Setup for the existing alignment.
 * @param dispatch Dispatch for the CreateSequenceAlignmentsModal state.
 * @constructor
 */
export default function UseCurrentVersionTable({
  alignmentSetup,
  dispatch,
}: {
  alignmentSetup: AlignmentSetup;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  let items: Array<AlignmentInput> = alignmentSetup.items;
  let template: AlignmentInput;
  if (isTemplateSetup(alignmentSetup)) {
    template = alignmentSetup.template;
    items = [template, ...alignmentSetup.items];
  }

  return (
    <Table numRows={items.length} overflowVisible={true}>
      <Column
        header="Sequence"
        renderCell={(rowIndex: number) => (
          <UseCurrentVersionTableSequenceCell
            input={items[rowIndex]}
            dispatch={dispatch}
            isTemplate={items[rowIndex] === template}
          />
        )}
      />
      <Column
        header="Use Latest Version"
        renderCell={(rowIndex: number) => (
          <UseCurrentVersionTableCheckboxCell input={items[rowIndex]} dispatch={dispatch} />
        )}
      />
    </Table>
  );
}
